import { Injectable, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { PreviewSlide } from '../models/preview/preview.models';
import { SlideEditOverride } from '../models/preview/preview-overrides.models';
import { ReportBlockData, ReportBlockSpec } from './report-blocks.service';
import {
	REPORT_INLINE_TEXT_LIMITS,
	ReportInlineEditService,
	ReportInlineEditTarget,
	slideTextFields
} from './report-inline-edit.service';
import { isQuoteBlock } from './report-quote';
import {
	REPORT_QUOTED_FIGURE_LIMITS,
	ReportFigure,
	ReportFigureFlag,
	ReportQuotedFigure,
	ReportQuotedFigures,
	figurePlace,
	hasQuotableNumbers,
	quotesWholePage,
	reconcileQuoted,
	recordOutlived,
	replaceQuoted,
	scopedFigures,
	slideFigures,
	staleFlags,
	withPlaceRecord
} from './report-quoted-figures';
import { ReportSettingsModel } from './report-settings.service';
import { REPORT_TEXT_BLOCK_LIMITS } from './report-text-blocks';

/** A fixed section as the page has drawn it. */
export interface ReportFigureSection {
	/** Its slide as last built: its text fields and its KPI tiles. */
	slide: PreviewSlide;
	/** Its chart's numbers (`PreviewSlide.chartNumbers`), when it has one. */
	numbers: ReportBlockData | null;
}

/** What the editor page gives this service. */
export interface ReportFigureFlagsHost {
	/** The report on the page, or null before one has loaded. */
	readonly figureSettings: ReportSettingsModel | null;
	/** The fixed sections the page has drawn, in the order they stand. */
	readonly figureSections: ReportFigureSection[];
	/** The added charts as drawn, each with the spec it was drawn for. */
	readonly figureCharts: Array<{
		spec: ReportBlockSpec;
		block: ReportBlockData;
	}>;
	/** Every section on the page has drawn and every chart has been read. */
	readonly figuresSettled: boolean;
	/** Keep a new record, with no step of the history; the autosave writes it. */
	recordQuotedFigures(record: ReportQuotedFigures | undefined): void;
}

const NO_FLAGS: ReportFigureFlag[] = [];

/** The longest each place's text may be once a number in it is rewritten. */
const PLACE_TEXT_LIMITS = {
	sectionTitle: 2000,
	sectionSummary: 2000,
	coverTitle: REPORT_INLINE_TEXT_LIMITS.coverTitle,
	coverDescription: REPORT_INLINE_TEXT_LIMITS.coverDescription,
	textBlock: REPORT_TEXT_BLOCK_LIMITS.stored
} as const;

/**
 * The flags under the report's texts: which quoted number the live data has
 * moved on from, and what it is now (report-quoted-figures.ts), with Update
 * and Keep.
 *
 * Provided by the editor page and by nothing else, like the inline editing it
 * works beside, so the PDF and a share link draw the text as saved with no
 * flag anywhere. The page tells it when anything the flags are worked out from
 * changes — the report, a slide built, the charts read — and it records, with
 * no step of the history, what a text quotes the first time it can: when its
 * section has drawn, or, for the cover and the text blocks, once the whole page
 * has. A figure that moved before then is simply not recorded, and so is
 * never flagged.
 */
@Injectable()
export class ReportFigureFlagsService implements OnDestroy {
	private host: ReportFigureFlagsHost | null = null;
	private readonly flags = new BehaviorSubject<
		Record<string, ReportFigureFlag[]>
	>({});
	private recordQueued = false;
	private destroyed = false;

	constructor(@Optional() private edits: ReportInlineEditService | null) {}

	attach(host: ReportFigureFlagsHost): void {
		this.host = host;
		this.refresh();
	}

	/** The flags under the text at `place`, as they change. */
	flagsFor(place: string | null): Observable<ReportFigureFlag[]> {
		return this.flags.pipe(
			map(all => (place ? all[place] ?? NO_FLAGS : NO_FLAGS)),
			distinctUntilChanged(sameFlags)
		);
	}

	/**
	 * Something the flags are worked out from has changed. The flags follow at
	 * once; what the texts quote is recorded after, in a microtask, because
	 * recording puts a new report on the page, which calls this again.
	 */
	refresh(): void {
		if (!this.host || this.destroyed) {
			return;
		}
		this.flags.next(this.workOutFlags());
		if (!this.recordQueued) {
			this.recordQueued = true;
			queueMicrotask(() => {
				this.recordQueued = false;
				if (!this.destroyed) {
					this.recordWhatTextsQuote();
				}
			});
		}
	}

	/**
	 * What `text`, about to be kept at `target`, quotes: what the place quoted
	 * before and still says, and the rest matched against the figures as they
	 * are now. Null when the text has no number to quote; undefined when the
	 * page cannot tell — a section not drawn yet — and the record stays.
	 */
	quotedFiguresFor(
		target: ReportInlineEditTarget,
		text: string,
		record?: ReportQuotedFigures
	): ReportQuotedFigure[] | null | undefined {
		const place = figurePlace(target);
		const settings = this.host?.figureSettings;
		const figures = place ? this.figuresFor(place) : null;
		if (!place || !settings || !figures) {
			return undefined;
		}
		if (!hasQuotableNumbers(text)) {
			return null;
		}
		return reconcileQuoted(
			(record ?? settings.quotedFigures)?.[place] ?? [],
			text,
			figures,
			settings.language
		);
	}

	/**
	 * `settings` with what Ana's rewrite of a section's heading or summary
	 * quotes recorded, read against the slide she rewrote. Not a step of the
	 * history, like the rewrite itself.
	 */
	withSectionRewrite(
		settings: ReportSettingsModel,
		slide: PreviewSlide,
		overrides: SlideEditOverride[]
	): ReportSettingsModel {
		const fields = slideTextFields(slide);
		const rewrites = overrides.flatMap(override => {
			if (override.type !== 'text') {
				return [];
			}
			const field = [fields.title, fields.body].find(
				one =>
					one?.target.kind === 'section' &&
					one.target.widgetId === override.widgetId
			);
			return field ? [{ target: field.target, text: override.text }] : [];
		});
		return this.withRewrites(settings, rewrites);
	}

	/** `settings` with what each rewritten text now quotes recorded. */
	withRewrites(
		settings: ReportSettingsModel,
		rewrites: Array<{ target: ReportInlineEditTarget; text: string }>
	): ReportSettingsModel {
		let record = settings.quotedFigures;
		for (const rewrite of rewrites) {
			const place = figurePlace(rewrite.target);
			const figures = this.quotedFiguresFor(
				rewrite.target,
				rewrite.text,
				record
			);
			if (place && figures !== undefined) {
				record = withPlaceRecord(record, place, figures);
			}
		}
		return record === settings.quotedFigures
			? settings
			: { ...settings, quotedFigures: record };
	}

	/**
	 * Update: the number rewritten at its new value, written the way the text
	 * wrote it, as one step of the history. The value is the page's own
	 * reading of its data — no model is asked.
	 */
	update(flag: ReportFigureFlag): void {
		const settings = this.host?.figureSettings;
		const target = this.targetFor(flag.place);
		const text = settings ? this.placeText(settings, flag.place) : null;
		if (!settings || !target || text === null || !this.edits) {
			return;
		}
		const next = replaceQuoted(text, flag.quoted, flag.now);
		if (next === text || next.length > placeLimit(flag.place)) {
			return;
		}
		const record = (settings.quotedFigures?.[flag.place] ?? []).map(entry =>
			entry.key === flag.key && entry.quoted === flag.quoted
				? { key: entry.key, quoted: flag.now }
				: entry
		);
		this.edits.refreshFigures(target, next, record);
	}

	/** Keep: the number stays, and so does its flag, hidden, until the figure moves again. */
	keep(flag: ReportFigureFlag): void {
		const settings = this.host?.figureSettings;
		if (!settings) {
			return;
		}
		const record = (settings.quotedFigures?.[flag.place] ?? []).map(entry =>
			entry.key === flag.key && entry.quoted === flag.quoted
				? { ...entry, kept: flag.value }
				: entry
		);
		this.host!.recordQuotedFigures(
			withPlaceRecord(settings.quotedFigures, flag.place, record)
		);
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.flags.complete();
	}

	private workOutFlags(): Record<string, ReportFigureFlag[]> {
		const settings = this.host?.figureSettings;
		const all: Record<string, ReportFigureFlag[]> = {};
		if (!settings) {
			return all;
		}
		const page = this.pageFigures();
		for (const [place, record] of Object.entries(
			settings.quotedFigures ?? {}
		)) {
			const text = record?.length
				? this.placeText(settings, place)
				: null;
			const figures = text === null ? null : this.figuresFor(place, page);
			if (text === null || !figures) {
				continue;
			}
			const flags = staleFlags(
				place,
				text,
				record,
				figures,
				settings.language
			);
			if (flags.length) {
				all[place] = flags;
			}
		}
		return all;
	}

	/**
	 * Record what each text quotes where it has no record yet — or where the
	 * record no longer describes the text, which only an edit made outside the
	 * editor (the settings form) leaves behind. A text with no number to quote
	 * gets none; one whose numbers matched nothing gets an empty record, so a
	 * figure that moved before it was first read is never matched later.
	 */
	private recordWhatTextsQuote(): void {
		const settings = this.host?.figureSettings;
		if (!settings) {
			return;
		}
		const page = this.pageFigures();
		const settled = this.host!.figuresSettled;
		let record = settings.quotedFigures;
		for (const place of this.places(settings)) {
			if (quotesWholePage(place) && !settled) {
				continue;
			}
			const text = this.placeText(settings, place);
			const figures = this.figuresFor(place, page);
			// A section drawn with nothing to quote yet — too few mentions, no
			// data — is read again once it has some.
			if (text === null || !figures?.length) {
				continue;
			}
			const before = record?.[place];
			if (
				before
					? !recordOutlived(before, text)
					: !hasQuotableNumbers(text)
			) {
				continue;
			}
			const next = reconcileQuoted(
				before ?? [],
				text,
				figures,
				settings.language
			);
			if (!before || !sameRecord(before, next)) {
				record = withPlaceRecord(record, place, next);
			}
		}
		if (record !== settings.quotedFigures) {
			this.host!.recordQuotedFigures(record);
		}
	}

	/** Every text on the page that can quote a figure. */
	private places(settings: ReportSettingsModel): string[] {
		const places = ['cover:title', 'cover:description'];
		for (const section of this.host?.figureSections ?? []) {
			const fields = slideTextFields(section.slide);
			for (const field of [fields.title, fields.body]) {
				const place = figurePlace(field?.target);
				if (place) {
					places.push(place);
				}
			}
		}
		for (const block of settings.textBlocks ?? []) {
			const place =
				block.kind === 'divider'
					? null
					: figurePlace({ kind: 'text_block', id: block.id });
			if (place) {
				places.push(place);
			}
		}
		return places.slice(0, REPORT_QUOTED_FIGURE_LIMITS.places);
	}

	/** The figures a text at `place` may quote; null when its section is not drawn. */
	private figuresFor(
		place: string,
		page: ReportFigure[] = this.pageFigures()
	): ReportFigure[] | null {
		if (quotesWholePage(place)) {
			return page;
		}
		const section = this.sectionOf(place);
		return section ? sectionFigures(section) : null;
	}

	/** Every figure on the page, each keyed by where it is drawn. */
	private pageFigures(): ReportFigure[] {
		const sections = (this.host?.figureSections ?? []).flatMap(section =>
			scopedFigures(section.slide.slideType, sectionFigures(section))
		);
		const charts = this.host?.figureCharts ?? [];
		const count = new Map<string, number>();
		for (const chart of charts) {
			count.set(
				chart.spec.block_id,
				(count.get(chart.spec.block_id) ?? 0) + 1
			);
		}
		// A chart is keyed by its block. Two of one block would share their
		// keys, and a removed one would hand its record to the other.
		const added = charts
			.filter(
				chart =>
					count.get(chart.spec.block_id) === 1 &&
					!isQuoteBlock(chart.spec)
			)
			.flatMap(chart =>
				scopedFigures(
					`chart:${chart.spec.block_id}`,
					slideFigures(chart.block)
				)
			);
		return [...sections, ...added];
	}

	private sectionOf(place: string): ReportFigureSection | null {
		const id = place.split(':')[1];
		return (
			this.host?.figureSections.find(
				section => section.slide.slideType === id
			) ?? null
		);
	}

	/** The text as the page draws it now; null when it is not on the page. */
	private placeText(
		settings: ReportSettingsModel,
		place: string
	): string | null {
		const [kind, part] = place.split(':');
		if (kind === 'cover') {
			return part === 'title'
				? settings.coverTitle
				: part === 'description'
				? settings.coverDescription
				: null;
		}
		if (kind === 'text') {
			const id = place.slice('text:'.length);
			const block = settings.textBlocks?.find(one => one.id === id);
			return block && block.kind !== 'divider' ? block.text : null;
		}
		const target = this.targetFor(place);
		const section = this.sectionOf(place);
		if (target?.kind !== 'section' || !section) {
			return null;
		}
		const item = settings.contentItems.find(
			one => one.id === target.sectionId
		);
		const widget = section.slide.widgets.find(
			one => one.id === target.widgetId
		);
		const override = item?.widgetOverrides?.[target.widgetId]?.text;
		const drawn =
			widget && 'text' in widget.content ? widget.content.text : null;
		return override ?? drawn ?? null;
	}

	/** Where the inline editing writes the text at `place`. */
	private targetFor(place: string): ReportInlineEditTarget | null {
		// `cover:<part>`, `text:<id>` and `section:<id>:<part>`.
		const [kind, first, second] = place.split(':');
		if (
			kind === 'cover' &&
			(first === 'title' || first === 'description')
		) {
			return { kind: 'cover', part: first };
		}
		if (kind === 'text') {
			return { kind: 'text_block', id: place.slice('text:'.length) };
		}
		const section = kind === 'section' ? this.sectionOf(place) : null;
		const fields = section ? slideTextFields(section.slide) : null;
		const field =
			second === 'title'
				? fields?.title
				: second === 'summary'
				? fields?.body
				: null;
		return field?.target.kind === 'section' ? field.target : null;
	}
}

/** A section's own figures: its KPI tiles and its chart's numbers. */
function sectionFigures(section: ReportFigureSection): ReportFigure[] {
	return [...slideFigures(section.slide), ...slideFigures(section.numbers)];
}

function placeLimit(place: string): number {
	if (place === 'cover:title') {
		return PLACE_TEXT_LIMITS.coverTitle;
	}
	if (place === 'cover:description') {
		return PLACE_TEXT_LIMITS.coverDescription;
	}
	if (place.startsWith('text:')) {
		return PLACE_TEXT_LIMITS.textBlock;
	}
	return place.endsWith(':title')
		? PLACE_TEXT_LIMITS.sectionTitle
		: PLACE_TEXT_LIMITS.sectionSummary;
}

function sameRecord(
	left: readonly ReportQuotedFigure[],
	right: readonly ReportQuotedFigure[]
): boolean {
	return JSON.stringify(left) === JSON.stringify(right);
}

function sameFlags(
	left: ReportFigureFlag[],
	right: ReportFigureFlag[]
): boolean {
	return (
		left === right ||
		(left.length === right.length &&
			left.every(
				(flag, at) =>
					flag.key === right[at].key &&
					flag.quoted === right[at].quoted &&
					flag.now === right[at].now &&
					flag.value === right[at].value
			))
	);
}
